import {useContext, useEffect} from 'react';
import {MessageDisplayerUtility} from "../MessageDisplayerUtility";
import {LeaveWithoutSavingWarningUtility} from "../LeaveWithoutSavingWarningUtility";
import {GlobalContext} from "../../admin-frame/AdminFrame";
import {useDebouncedFunction} from "./useDebouncedFunction";
import {useRenderableData} from "./useRenderableData";
import _ from "lodash";

// Engine for a form component for a data model. Tracks state of form and exposes form event handler functions (for field updates, save operations). Can fetch supporting data for the form on mount.
export function useModelFormEngine(
    initialModelSpecificState,
    initialModel,
    fetchSupportingData,
    validateForm,
    formStateToSaveData,
    save
) {
    const context = useContext(GlobalContext);
    const initialState = {
        id: initialModel ? initialModel.id : null,
        invalidFields: {},
        validationErrors: [],
        saved: true,
        supportingData: null,
        validating: false,
        ...initialModelSpecificState,
    }
    const formRenderableData = useRenderableData(initialState);

    const onClickSave = useDebouncedFunction(() => {
        formRenderableData.updateAndRender({showOverlayLoadingIndicator: true})
        validateForm(handleValidationResult)
            .then(function (validationPasses) {
                if (validationPasses) {
                    let saveData = _.clone(formRenderableData);
                    if (formStateToSaveData) {
                        saveData = formStateToSaveData(formRenderableData)
                    }
                    save(saveData);
                }
            })
            .catch(function (error) {
                console.log("Error validating form: ", error);
                MessageDisplayerUtility.error("An error occurred while saving.")
            });
    });

    // on mount, load supporting data if there is any that needs to be loaded
    useEffect(function () {
        refreshSupportingData()
    }, []);

    function refreshSupportingData(additionalStateChange) {
        if (fetchSupportingData) {
            formRenderableData.updateAndRender({showOverlayLoadingIndicator: true})
            fetchSupportingData()
                .then(function (supportingDataState) {
                    let stateChange = {
                        ...supportingDataState,
                        showOverlayLoadingIndicator: false
                    }
                    if (additionalStateChange) {
                        stateChange = {
                            ...additionalStateChange,
                            ...stateChange
                        }
                    }
                    formRenderableData.updateAndRender(stateChange)
                })
                .catch(function (error) {
                    formRenderableData.updateAndRender({showOverlayLoadingIndicator: false})
                    console.log("Error loading form data: ", error);
                    MessageDisplayerUtility.error("An error occurred while loading form data.")
                });
        } else {
            formRenderableData.updateAndRender({showOverlayLoadingIndicator: false})
        }
    }

    function updateAndRenderUnsavedData(dataUpdate) {
        LeaveWithoutSavingWarningUtility.enableLeaveWithoutSavingWarnings(context);
        formRenderableData.updateAndRender({
            ...dataUpdate,
            saved: false
        })
    }

    function handleValidationResult(validationErrors, invalidFields, resolve) {
        if (validationErrors.length > 0) {
            let invalidFieldsUpdate = {};
            for (let y = 0; y < invalidFields.length; y++) {
                invalidFieldsUpdate[`${invalidFields[y]}_invalid`] = true;
            }
            formRenderableData.updateAndRender({
                showOverlayLoadingIndicator: false,
                invalidFields: invalidFieldsUpdate,
                validationErrors: validationErrors
            })
            // display validation errors
            for (let e = 0; e < validationErrors.length; e++) {
                MessageDisplayerUtility.error(validationErrors[e]);
            }
            resolve(false);
        } else {
            formRenderableData.updateAndRender({
                showOverlayLoadingIndicator: false,
                invalidFields: {},
                validationErrors: []
            })
            resolve(true);
        }
    }

    function getFormFieldClasses(originalClasses, fieldName) {
        let returnClasses = originalClasses;
        if (Array.isArray(fieldName)) {
            let allValid = true;
            for (let f = 0; f < fieldName.length; f++) {
                if (formRenderableData.invalidFields[`${fieldName[f]}_invalid`]) {
                    allValid = false;
                    break;
                }
            }
            if (!allValid) {
                returnClasses += ' invalid';
            }
        } else {
            if (formRenderableData.invalidFields[`${fieldName}_invalid`]) {
                returnClasses += ' invalid';
            }
        }
        return returnClasses;
    }

    function handleTextFieldChange(fieldName, event) {
        if (event && event.target) {
            updateAndRenderUnsavedData({[fieldName]: event.target.value})
        }
    }

    function handleRadioOptionChange(fieldName, selectedOption) {
        if (selectedOption) {
            updateAndRenderUnsavedData({[fieldName]: selectedOption})
        }
    }

    // todo remove and replace with selectutility approach in all forms
    function handleSelectChange(fieldName, selectedOption) {
        if (selectedOption) {
            updateAndRenderUnsavedData({[fieldName]: selectedOption})
        }
    }

    function handleCheckboxChange(fieldName, value) {
        updateAndRenderUnsavedData({[fieldName]: value})
    }

    function forceValueToNumeric(fieldName) {
        if (formRenderableData[fieldName] !== parseInt(formRenderableData[fieldName]) || 0) {
            updateAndRenderUnsavedData({[fieldName]: parseInt(formRenderableData[fieldName]) || 0})
        }
    }

    return {
        renderableData: formRenderableData,
        onClickSave: onClickSave,
        getFormFieldClasses: getFormFieldClasses,
        handleTextFieldChange: handleTextFieldChange,
        handleRadioOptionChange: handleRadioOptionChange,
        handleCheckboxChange: handleCheckboxChange,
        handleSelectChange: handleSelectChange,
        forceValueToNumeric: forceValueToNumeric,
        updateAndRenderUnsavedData: updateAndRenderUnsavedData,
        refreshSupportingData: refreshSupportingData
    };
}
